import { assetsHttp, inferenceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { i18n } from '@/shared/i18n';
import { ContextsService as InferenceContextsService } from '@inference.engine/api-client/web';
import { DocumentConversionService } from '@inference.engine/assets-api-client/web';

/**
 * Custom error for file upload/conversion cancellations.
 * Extends Error with a specific name to distinguish from other errors.
 */
export class FileCancellationError extends Error {
  // eslint-disable-next-line i18next/no-literal-string -- default message of a cancellation error the callers branch on by instance
  constructor(message = 'Operation cancelled') {
    super(message);
    this.name = 'FileCancellationError';
  }
}

export const getChatInfo = async ({ contextId }: { contextId: string }) => {
  try {
    const response = await InferenceContextsService.getContext({
      client: inferenceHttp,
      path: { contextId },
    });
    if (!response.data) {
      throw new Error('No chat info returned');
    }
    return response.data;
  } catch (error) {
    throw new Error(
      `Failed to get chat info: ${error instanceof Error ? error.message : 'Unknown error'}`,
      { cause: error },
    );
  }
};

type GetChatHistoryOptions = Parameters<typeof InferenceContextsService.getExchanges>[0];
type GetChatHistoryParams = GetChatHistoryOptions['path'] & GetChatHistoryOptions['query'];

export const getChatHistory = async (params: GetChatHistoryParams) => {
  const response = await InferenceContextsService.getExchanges({
    client: inferenceHttp,
    path: { contextId: params.contextId },
    query: {
      skip: params.skip,
      take: params.take,
      includeActiveAgent: params.includeActiveAgent,
    },
  });

  if (!response.data) {
    throw new Error('No data found for the given Session ID');
  }

  return response.data;
};

// `createChatId` lifted to `@/shared/api/agent-runner` so non-chat features
// (brief-generation, etc.) don't cross into chats. Re-exported here for
// backward compat — existing chat-internal callers keep working unchanged.
export { createChatId } from '@/shared/api/agent-runner';

export const getChatSuggestions = async ({
  agentId,
  chatId,
  count = 3,
  signal,
}: {
  agentId: string;
  chatId?: string;
  count?: number;
  signal?: AbortSignal;
}) => {
  try {
    const response = await InferenceContextsService.getSuggestions({
      client: inferenceHttp,
      query: { agentId, contextId: chatId, count },
      timeout: 20000,
      signal,
      // Suppress the generic axios error toast for this background fetch.
      // When the agent is soft-deleted, the API returns 410 — users already
      // see the AgentDeletedNotice banner; the toast would be redundant noise
      // (and in StrictMode could appear twice due to effect double-invocation).
      // The useAiChat onError path still shows a toast on user-initiated sends.
      // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
      headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
    });

    if (!response.data) {
      throw new Error('No suggestions data returned');
    }

    return response.data;
  } catch (error) {
    throw new Error(
      `Failed to get suggestions: ${error instanceof Error ? error.message : 'Unknown error'}`,
      { cause: error },
    );
  }
};

// Re-exported from shared — domain-agnostic asset operations.
// `getFileDownloadUrl` + `markAssetPublic` extracted to `shared/api/`,
// `getFileUploadUrl` + `uploadFileToS3` extracted to `shared/lib/file-upload`,
// to keep the chats feature boundary clean (consumers in other features
// no longer cross into chats just for file ops).
export { getFileDownloadUrl, markAssetPublic } from '@/shared/api/assets.api';

interface ConversionStatusResponse {
  status: 'waiting' | 'active' | 'completed' | 'failed';
  assetId?: string;
  pdfSize?: number;
  error?: string;
}

const CONVERSION_POLL_INTERVAL = 3000;
const CONVERSION_TIMEOUT = 2 * 60 * 1000; // 2 minutes

export const convertToPdf = async (file: File, signal?: AbortSignal): Promise<string> => {
  const response = await DocumentConversionService.convertToPdf({
    client: assetsHttp,
    body: { file },
    signal,
  });

  if (!response.data?.jobId) {
    throw new Error(i18n.t('chats:chats.chatsApi.conversionNoJobId'));
  }

  return response.data.jobId;
};

const getConversionStatus = async (jobId: string): Promise<ConversionStatusResponse> => {
  const response = await DocumentConversionService.getConversionStatus({
    client: assetsHttp,
    path: { jobId },
  });

  if (!response.data) {
    throw new Error(i18n.t('chats:chats.chatsApi.conversionNoStatus'));
  }

  return response.data as ConversionStatusResponse;
};

export const pollConversionUntilDone = (
  jobId: string,
  signal?: AbortSignal,
): Promise<{ assetId: string; pdfSize?: number }> => {
  return new Promise((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const startTime = Date.now();

    const cleanup = () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      signal?.removeEventListener('abort', onAbort);
    };

    const onAbort = () => {
      cleanup();
      // eslint-disable-next-line i18next/no-literal-string -- cancellation message the callers branch on by instance, never toasted
      reject(new FileCancellationError('Conversion cancelled'));
    };

    if (signal?.aborted) {
      // eslint-disable-next-line i18next/no-literal-string -- cancellation message the callers branch on by instance, never toasted
      reject(new FileCancellationError('Conversion cancelled'));
      return;
    }

    signal?.addEventListener('abort', onAbort);

    const poll = async () => {
      try {
        if (Date.now() - startTime >= CONVERSION_TIMEOUT) {
          cleanup();
          reject(new Error(i18n.t('chats:chats.chatsApi.conversionTimedOut')));
          return;
        }

        const status = await getConversionStatus(jobId);

        switch (status.status) {
          case 'completed':
            cleanup();
            if (status.assetId) {
              resolve({ assetId: status.assetId, pdfSize: status.pdfSize });
            } else {
              reject(new Error(i18n.t('chats:chats.chatsApi.conversionNoAssetId')));
            }
            break;
          case 'failed':
            cleanup();
            reject(new Error(status.error ?? i18n.t('chats:chats.chatsApi.conversionFailed')));
            break;
          default:
            timeoutId = setTimeout(poll, CONVERSION_POLL_INTERVAL);
        }
      } catch (err) {
        cleanup();
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    };

    void poll();
  });
};

export interface GenerateChatTitleParams {
  contextId: string;
  prompt: string;
}

export async function generateChatTitle(params: GenerateChatTitleParams) {
  const response = await InferenceContextsService.generateTitle({
    client: inferenceHttp,
    path: { contextId: params.contextId },
    body: { prompt: params.prompt },
  });

  if (!response.data?.title) {
    throw new Error('Failed to generate title: no title in response');
  }

  return response.data.title;
}

export const updateChatTitle = async (contextId: string, updates: { title?: string }) => {
  const response = await InferenceContextsService.updateMetadata({
    client: inferenceHttp,
    path: { contextId },
    body: updates,
  });

  if (!response.data) {
    throw new Error('No data returned');
  }

  return response.data;
};
